import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import type { ImportJobStagedImage } from '~entities/import-job/api/types';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

export interface ClassPhotoMapping {
  rowIndex: number;
  classTitle: string;
  file?: File;
  staged?: ImportJobStagedImage;
  /** True when the file came from zip extraction (not manual pick). */
  fromZip?: boolean;
}

interface Step2PhotosProps {
  classTitles: Array<{ rowIndex: number; title: string }>;
  mappings: ClassPhotoMapping[];
  onChange: (mappings: ClassPhotoMapping[]) => void;
  onNext: () => void | Promise<void>;
  onBack: () => void;
  onSkip: () => void;
}

type ExtractedZipImage = {
  filename: string;
  file: File;
  byteLength: number;
};

type ZipExtractResult = {
  zipName: string;
  extracted: ExtractedZipImage[];
  matched: Array<{
    filename: string;
    classTitle: string;
    rowIndex: number;
    byteLength: number;
  }>;
  unmatched: Array<{ filename: string; byteLength: number }>;
};

function normalizeTitle(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function mimeFromFilename(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'jpg':
    case 'jpeg':
    default:
      return 'image/jpeg';
  }
}

function isZipFile(file: File): boolean {
  if (/\.zip$/i.test(file.name)) return true;
  const type = file.type.toLowerCase();
  return (
    type === 'application/zip' ||
    type === 'application/x-zip' ||
    type === 'application/x-zip-compressed' ||
    type === 'application/octet-stream'
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Phase 1 — unzip only. Returns real File blobs with size > 0.
 * Nothing is mapped or uploaded here.
 */
async function extractImagesFromZip(zipFile: File): Promise<ExtractedZipImage[]> {
  const zip = await JSZip.loadAsync(zipFile);
  const extracted: ExtractedZipImage[] = [];

  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;
    if (path.includes('__MACOSX/') || path.includes('__macosx/')) continue;

    const base = path.split('/').pop() ?? path;
    if (base.startsWith('._')) continue;
    if (!/\.(jpe?g|png|webp|gif)$/i.test(base)) continue;

    const blob = await entry.async('blob');
    if (!blob || blob.size === 0) continue;

    const file = new File([blob], base, { type: mimeFromFilename(base) });
    if (file.size <= 0) continue;

    extracted.push({
      filename: base,
      file,
      byteLength: file.size,
    });
  }

  return extracted;
}

/**
 * Phase 2 — map extracted files to class titles by filename.
 */
function mapExtractedToClasses(
  extracted: ExtractedZipImage[],
  classTitles: Array<{ rowIndex: number; title: string }>,
): Pick<ZipExtractResult, 'matched' | 'unmatched'> {
  const matched: ZipExtractResult['matched'] = [];
  const unmatched: ZipExtractResult['unmatched'] = [];
  const usedRows = new Set<number>();

  for (const item of extracted) {
    const nameKey = normalizeTitle(item.filename.replace(/\.[^.]+$/, ''));
    const row = classTitles.find(
      (c) =>
        !usedRows.has(c.rowIndex) && normalizeTitle(c.title) === nameKey,
    );
    if (!row) {
      unmatched.push({
        filename: item.filename,
        byteLength: item.byteLength,
      });
      continue;
    }
    usedRows.add(row.rowIndex);
    matched.push({
      filename: item.filename,
      classTitle: row.title,
      rowIndex: row.rowIndex,
      byteLength: item.byteLength,
    });
  }

  return { matched, unmatched };
}

export function Step2Photos({
  classTitles,
  mappings,
  onChange,
  onNext,
  onBack,
  onSkip,
}: Step2PhotosProps) {
  const [extracting, setExtracting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [extractResult, setExtractResult] = useState<ZipExtractResult | null>(
    null,
  );
  const [pendingZipFile, setPendingZipFile] = useState<File | null>(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);

  const busy = extracting || continuing;

  const handleContinue = async () => {
    setContinuing(true);
    try {
      await onNext();
    } finally {
      setContinuing(false);
    }
  };

  const hasExistingZipUpload =
    extractResult != null || mappings.some((m) => m.fromZip);

  const setRowFile = (rowIndex: number, classTitle: string, file: File) => {
    const next = mappings.filter((m) => m.rowIndex !== rowIndex);
    next.push({ rowIndex, classTitle, file, fromZip: false });
    onChange(next);
  };

  const processZipFile = useCallback(
    async (zipFile: File) => {
      setExtracting(true);
      setExtractResult(null);
      try {
        // 1) Extract first — only real non-empty image files continue.
        const extracted = await extractImagesFromZip(zipFile);
        if (extracted.length === 0) {
          setZipError(
            `We couldn't find any usable images in "${zipFile.name}". Make sure the archive contains .jpg, .png, .webp, or .gif files (not folders or other file types).`,
          );
          setExtractResult({
            zipName: zipFile.name,
            extracted: [],
            matched: [],
            unmatched: [],
          });
          // Clear previous zip mappings — this zip replaces any prior zip.
          onChange(mappings.filter((m) => !m.fromZip));
          return;
        }

        // 2) Map extracted files to class titles.
        const { matched, unmatched } = mapExtractedToClasses(
          extracted,
          classTitles,
        );

        const byName = new Map(extracted.map((e) => [e.filename, e]));
        // Replace prior zip mappings; keep manual picks unless overwritten.
        const nextByRow = new Map(
          mappings
            .filter((m) => !m.fromZip)
            .map((m) => [m.rowIndex, m] as const),
        );
        for (const m of matched) {
          const extractedFile = byName.get(m.filename);
          if (!extractedFile) continue;
          nextByRow.set(m.rowIndex, {
            rowIndex: m.rowIndex,
            classTitle: m.classTitle,
            file: extractedFile.file,
            fromZip: true,
          });
        }
        onChange([...nextByRow.values()]);

        const result: ZipExtractResult = {
          zipName: zipFile.name,
          extracted,
          matched,
          unmatched,
        };
        setExtractResult(result);

        if (matched.length === 0) {
          toast.message('Zip extracted, but no class matches', {
            description: `Found ${extracted.length} image(s). Name files like the class title (e.g. "Ballet 1.jpg").`,
          });
        } else if (unmatched.length > 0) {
          toast.success(`Ready to upload ${matched.length} photo(s)`, {
            description: `${unmatched.length} extracted file(s) did not match a class and were skipped.`,
          });
        } else {
          toast.success(`Ready to upload ${matched.length} photo(s)`, {
            description: `Extracted and matched from ${zipFile.name}.`,
          });
        }
      } catch {
        setZipError(
          `We couldn't read "${zipFile.name}". It may be corrupted, password-protected, or not a valid .zip file. You can retry, add photos to each class manually, or continue without photos.`,
        );
        setExtractResult(null);
      } finally {
        setExtracting(false);
      }
    },
    [classTitles, mappings, onChange],
  );

  const requestZipProcess = useCallback(
    (zipFile: File) => {
      if (!isZipFile(zipFile)) {
        toast.error('That file type is not supported for class photos', {
          description: `"${zipFile.name}" is not a .zip file. Drop a .zip of class photos, or add an image to each class below.`,
        });
        return;
      }

      // Only one zip mapping at a time — confirm before replacing.
      if (hasExistingZipUpload) {
        setPendingZipFile(zipFile);
        setOverrideDialogOpen(true);
        return;
      }

      void processZipFile(zipFile);
    },
    [hasExistingZipUpload, processZipFile],
  );

  const handleOverrideDialogChange = (open: boolean) => {
    setOverrideDialogOpen(open);
    if (!open) setPendingZipFile(null);
  };

  const handleOverrideNo = () => {
    setOverrideDialogOpen(false);
    setPendingZipFile(null);
  };

  const handleOverrideYes = () => {
    const zipFile = pendingZipFile;
    setOverrideDialogOpen(false);
    setPendingZipFile(null);
    if (zipFile) void processZipFile(zipFile);
  };

  const zipDropzone = useDropzone({
    onDrop: (accepted, rejected) => {
      const fromAccepted = accepted[0];
      const fromRejected = rejected[0]?.file;
      const candidate = fromAccepted ?? fromRejected;
      if (candidate && isZipFile(candidate)) {
        requestZipProcess(candidate);
        return;
      }
      if (rejected.length > 0 || accepted.length > 0) {
        const name = (fromRejected ?? fromAccepted)?.name;
        toast.error('That file type is not supported for class photos', {
          description: name
            ? `"${name}" is not a .zip file. Drop a .zip of class photos, or add an image to each class below.`
            : 'Drop a .zip of class photos, or add an image to each class below.',
        });
      }
    },
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/octet-stream': ['.zip'],
    },
    multiple: false,
    disabled: busy || overrideDialogOpen || classTitles.length === 0,
  });

  return (
    <Card className='mx-auto max-w-4xl'>
      <CardContent className='space-y-6 p-6'>
        <div>
          <h2 className='text-xl font-semibold'>Class photos</h2>
          <p className='mt-2 text-muted-foreground'>
            {classTitles.length === 0
              ? 'Finish mapping classes first to enable class photos. Skip this step if you are not adding classes in this wizard.'
              : 'Optional. Add a photo per class, or drop a zip — we extract images first, then match filenames to class titles before upload. A 1:1 photo count is not required. Only one zip upload is kept at a time.'}
          </p>
        </div>

        <div
          {...zipDropzone.getRootProps()}
          className={`rounded-lg border border-dashed p-6 text-center ${
            busy || classTitles.length === 0
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer'
          }`}
        >
          <input {...zipDropzone.getInputProps()} />
          <p className='text-sm'>
            {classTitles.length === 0
              ? 'Class photo upload disabled — finish mapping classes first'
              : extracting
                ? 'Extracting images from zip…'
                : 'Drop a .zip of class photos here'}
          </p>
          <p className='mt-1 text-xs text-muted-foreground'>
            Images are unzipped and verified before they are mapped or uploaded.
          </p>
        </div>

        {extractResult && (
          <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm font-medium'>
                Extracted from {extractResult.zipName}
              </p>
              <Badge variant='secondary'>
                {extractResult.extracted.length} file
                {extractResult.extracted.length === 1 ? '' : 's'}
              </Badge>
              <Badge variant='outline'>
                {extractResult.matched.length} matched
              </Badge>
              {extractResult.unmatched.length > 0 && (
                <Badge variant='outline'>
                  {extractResult.unmatched.length} skipped
                </Badge>
              )}
            </div>

            {extractResult.matched.length > 0 && (
              <div className='space-y-1'>
                <p className='text-xs font-medium text-muted-foreground'>
                  Ready to upload
                </p>
                <ul className='max-h-40 space-y-1 overflow-y-auto text-sm'>
                  {extractResult.matched.map((item) => (
                    <li
                      key={`matched-${item.rowIndex}-${item.filename}`}
                      className='flex flex-wrap items-baseline justify-between gap-2 rounded-md border bg-background px-3 py-1.5'
                    >
                      <span className='min-w-0 truncate'>
                        <span className='font-medium'>{item.filename}</span>
                        <span className='text-muted-foreground'>
                          {' '}
                          → {item.classTitle}
                        </span>
                      </span>
                      <span className='shrink-0 text-xs text-muted-foreground'>
                        {formatBytes(item.byteLength)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {extractResult.unmatched.length > 0 && (
              <div className='space-y-1'>
                <p className='text-xs font-medium text-muted-foreground'>
                  Extracted but not matched (skipped)
                </p>
                <ul className='max-h-28 space-y-1 overflow-y-auto text-sm'>
                  {extractResult.unmatched.map((item, index) => (
                    <li
                      key={`unmatched-${item.filename}-${index}`}
                      className='flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-dashed px-3 py-1.5 text-muted-foreground'
                    >
                      <span className='min-w-0 truncate'>{item.filename}</span>
                      <span className='shrink-0 text-xs'>
                        {formatBytes(item.byteLength)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className='max-h-96 space-y-3 overflow-y-auto'>
          {classTitles.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No classes mapped yet. Finish the classes step to enable photos, or
              skip and add photos later from each class page.
            </p>
          ) : (
            classTitles.map((row) => {
              const mapping = mappings.find((m) => m.rowIndex === row.rowIndex);
              return (
                <ClassPhotoRow
                  key={row.rowIndex}
                  rowIndex={row.rowIndex}
                  title={row.title}
                  file={mapping?.file ?? null}
                  staged={mapping?.staged}
                  fromZip={mapping?.fromZip}
                  onFile={(file) => setRowFile(row.rowIndex, row.title, file)}
                />
              );
            })
          )}
        </div>

        <div className='flex justify-between'>
          <Button variant='outline' onClick={onBack} disabled={busy}>
            Back
          </Button>
          <div className='flex gap-2'>
            <Button variant='ghost' onClick={onSkip} disabled={busy}>
              Skip for now
            </Button>
            <Button onClick={() => void handleContinue()} disabled={busy}>
              {continuing ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving…
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        </div>
      </CardContent>

      <AlertDialog
        open={overrideDialogOpen}
        onOpenChange={handleOverrideDialogChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Do you understand?</AlertDialogTitle>
            <AlertDialogDescription>
              Multiple zip uploads are not kept at the same time. Uploading{' '}
              {pendingZipFile ? (
                <span className='font-medium text-foreground'>
                  {pendingZipFile.name}
                </span>
              ) : (
                'this zip'
              )}{' '}
              will replace the photos and class mappings from your previous zip
              upload.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleOverrideNo}>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleOverrideYes();
              }}
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={zipError != null}
        onOpenChange={(open) => {
          if (!open) setZipError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>We couldn&apos;t use that zip</AlertDialogTitle>
            <AlertDialogDescription>{zipError}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className='text-sm text-muted-foreground'>
            You can:
            <ul className='ml-4 mt-1 list-disc space-y-1'>
              <li>Retry with a corrected zip file.</li>
              <li>Add a photo to each class manually below.</li>
              <li>Continue without photos and add them later.</li>
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setZipError(null)}>
              Add photos manually
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                setZipError(null);
                onSkip();
              }}
            >
              Continue without photos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function ClassPhotoRow({
  rowIndex,
  title,
  file,
  staged,
  fromZip,
  onFile,
}: {
  rowIndex: number;
  title: string;
  file: File | null;
  staged?: ImportJobStagedImage;
  fromZip?: boolean;
  onFile: (f: File) => void;
}) {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0) {
        const name = rejected[0]?.file?.name;
        toast.error('That file type is not supported for class photos', {
          description: name
            ? `"${name}" is not a valid image. Use .jpg, .jpeg, .png, or .webp.`
            : 'Use .jpg, .jpeg, .png, or .webp.',
        });
        return;
      }
      const f = accepted[0];
      if (f) onFile(f);
    },
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    multiple: false,
  });

  return (
    <div className='flex items-center justify-between gap-4 rounded-md border p-3'>
      <div className='min-w-0'>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='truncate font-medium'>{title}</div>
          {fromZip && file ? (
            <Badge variant='secondary' className='shrink-0'>
              From zip
            </Badge>
          ) : null}
        </div>
        <div className='text-xs text-muted-foreground'>
          Row {rowIndex + 1}
          {file ? ` · ${formatBytes(file.size)}` : ''}
        </div>
      </div>
      <div {...getRootProps()} className='shrink-0'>
        <input {...getInputProps()} />
        <Button type='button' variant='outline' size='sm'>
          {file
            ? file.name
            : staged?.originalFilename
              ? `Saved: ${staged.originalFilename}`
              : 'Add photo'}
        </Button>
      </div>
    </div>
  );
}
